import {ArrowDownward, ArrowUpward} from "@mui/icons-material";
import {Avatar, Card, IconButton, Stack, Typography} from "@mui/material";
import React, {cloneElement, useCallback, useState} from "react";
import {useAuthState} from "react-firebase-hooks/auth";
import {addPlace, addTrip, auth, PlaceModel, swapOrderOfPlannedPlaces, TripModel} from "../../firebase/index.js";
import {useInMemoryTrip} from "../../redux/InMemoryTripReducer.js";
import type {APXBaseOption, APXOptions} from "../../reusables/APXMoreMenuWithPopover.jsx";
import APXMoreMenuWithPopover from "../../reusables/APXMoreMenuWithPopover.jsx";
import {eatery, photography, red_900, shop, transport} from "../../reusables/js/APXTheme.js";
import {ReactComponent as FoodNonVegIcon} from "../../svg/food_non_veg.svg";
import {ReactComponent as FoodVegIcon} from "../../svg/food_veg.svg";
import {ReactComponent as NoPhotographyIcon} from "../../svg/no_photography.svg";
import {ReactComponent as ShoppingIcon} from "../../svg/shopping.svg";
import AddNewPlaceBSL from "./AddNewPlaceBSL.jsx";
import AddNewTransportBSL from "./AddNewTransportBSL.jsx";
import AnchorSelectorBSL, {useAnchorSelectorBSL, useAnchorSelectorBSLForDuplication} from "./AnchorSelectorBSL.jsx";
import {ClosedOnDaysOfWeek} from "./DaySelector.jsx";
import {transportModes} from "./TransportSelector.jsx";


export default function PlaceLayout({item}: { item: PlaceModel }) {
	  const place: PlaceModel = item;
	  
	  //  const {currentItem, currentOption, setCurrentItem, setCurrentOption, showBottomSheet, setShowBottomSheet} = useCurrentSelectedOption({});
	  const [showBottomSheet, setShowBottomSheet] = useState(false);
	  
	  const inMemoryTrip: TripModel = useInMemoryTrip();
	  const uid = useAuthState(auth)[0]?.uid;
	  
	  const {showAnchorSelectorBottomSheet, setShowAnchorSelectorBottomSheet} = useAnchorSelectorBSL();
	  
	  const {showAnchorSelectorBottomSheetForDuplication, setShowAnchorSelectorBottomSheetForDuplication} = useAnchorSelectorBSLForDuplication();
	  
	  const placeMenuOptions: APXOptions = {
			 fulfil: {
					label: "Mark as fulfilled",
					needsConfirmation: true,
					keepDisabled: place.isFulfilled,
					onClick: useCallback(() => {
						  if (/*FALSY->*/ !!!uid) return;
						  place.setAsFulfilled();
						  const successfullyJustSet = inMemoryTrip.setAsUneditable(); /* TRIP BECOMES UNEDITABLE WHEN ONE PLACE OF IT IS SET AS FULFILLED */
						  successfullyJustSet && addTrip(uid, inMemoryTrip.tripKey, inMemoryTrip);
						  addPlace(uid, inMemoryTrip.itineraryKey, place.anchoredOnDate, place.placeKey, place);
					}, [uid])
			 },
			 anchor: {
					label: "Anchor to a date",
					needsConfirmation: false,
					keepDisabled: /*TRUTHY->*/ !!place.anchoredOnDate || place.isFulfilled,
					onClick: useCallback(() => {
						  if (/*FALSY->*/ !!!uid) return;
						  setShowAnchorSelectorBottomSheet(true);
					}, [uid])
			 },
			 moveToAnotherDate: {
					label: "Move to another date",
					needsConfirmation: false,
					keepDisabled: /*FALSY->*/ !!!place.anchoredOnDate || place.isFulfilled,
					onClick: useCallback(() => {
						  if (/*FALSY->*/ !!!uid) return;
						  setShowAnchorSelectorBottomSheet(true);
					}, [uid])
			 },
			 revisitOnAnotherDate: {
					label: "Revisit on another date",
					needsConfirmation: false,
					keepDisabled: /*FALSY->*/ !!!place.anchoredOnDate || place.isFulfilled,
					onClick: useCallback(() => {
						  if (/*FALSY->*/ !!!uid) return;
						  setShowAnchorSelectorBottomSheetForDuplication(true);
					}, [uid])
			 },
			 moveBackToUnassigned: {
					label: "Move back to unassigned",
					needsConfirmation: false,
					keepDisabled: /*FALSY->*/ !!!place.anchoredOnDate || place.isFulfilled,
					onClick: useCallback(() => {
						  if (/*FALSY->*/ !!!uid) return;
						  addPlace(uid, inMemoryTrip.itineraryKey, place.anchoredOnDate, place.placeKey, null);
						  place.setAnchoredOnDate(null);
						  addPlace(uid, inMemoryTrip.itineraryKey, place.anchoredOnDate, place.placeKey, place);
					}, [uid])
			 },
			 edit: {
					label: "Edit",
					needsConfirmation: false,
					keepDisabled: place.isFulfilled,
					onClick: useCallback(() => {
						  if (/*FALSY->*/ !!!uid) return;
						  setShowBottomSheet(true);
					}, [uid])
			 },
			 delete: {
					label: "Delete",
					needsConfirmation: true,
					keepDisabled: place.isFulfilled,
					onClick: useCallback(() => {
						  if (/*FALSY->*/ !!!uid) return;
						  addPlace(uid, inMemoryTrip.itineraryKey, place.anchoredOnDate, place.placeKey, null);
					}, [uid])
			 }
	  };
	  
	  return (
				 <Card variant={"outlined"}
						 sx={{
								p: 2, mt: 1, borderColor:
										  place.isEatery ? eatery : place.isShop ? shop : !place.isPhotographyAllowed ? photography :
													 place.isTransport ? transport : red_900,
								opacity: place.isFulfilled ? 0.75 : 1
						 }}>
						<Stack direction="row" justifyContent="space-between" alignItems="center">
							  {
										 place.isTransport && place.transportName && transportModes[place.transportName] &&
										 <>
												<Avatar sx={{width: 36, height: 36, mr: 2, backgroundColor: transport}}>
													  {cloneElement(transportModes[place.transportName].transportIcon, {style: {width: 24, height: 24, color: "white"}})}
												</Avatar>
												<Typography variant="caption" mr={2}>{place.transportName} to</Typography>
										 </>
							  }
							  <Typography variant="h6" flexGrow={1}>{place.name}</Typography>
							  <APXMoreMenuWithPopover options={placeMenuOptions}
															  onConfirmedOptionSelected={(option: APXBaseOption) => {
																	 option && option.onClick();
															  }}
															  disabled={inMemoryTrip.isCompleted || place.isFulfilled}
							  />
							  {
									 showBottomSheet ?
												place.isTransport ?
														  <AddNewTransportBSL uid={uid} onClose={() => setShowBottomSheet(false)} place={place}/>
														  :
														  <AddNewPlaceBSL uid={uid} onClose={() => setShowBottomSheet(false)} place={place}/>
												: null
							  }
							  {
										 showAnchorSelectorBottomSheet &&
										 <AnchorSelectorBSL uid={uid} onClose={() => setShowAnchorSelectorBottomSheet(false)} place={place}/>
							  }
							  {
										 showAnchorSelectorBottomSheetForDuplication &&
										 <AnchorSelectorBSL forDuplication={true} uid={uid} onClose={() => setShowAnchorSelectorBottomSheetForDuplication(false)} place={place}/>
							  }
						</Stack>
						{
							  /*NOT->*/ !place.isTransport &&
								  <Stack direction={{xs: "column", sm: "row"}} spacing={1} justifyContent="space-between" alignItems="center">
										 <Stack direction="row" spacing={0.5}>
												{
														  place.isShop &&
														  <Avatar sx={{width: 24, height: 24, backgroundColor: shop}}><ShoppingIcon/></Avatar>
												}
												{
														  place.isEatery &&
														  <Avatar sx={{width: 24, height: 24, backgroundColor: eatery}}>{place.isVeg ? <FoodVegIcon/> :
																	 <FoodNonVegIcon/>}</Avatar>
												}
												{
													  /*NOT->*/ !place.isPhotographyAllowed &&
														  <Avatar sx={{width: 24, height: 24, backgroundColor: photography}}><NoPhotographyIcon/></Avatar>
												}
										 </Stack>
										 <ClosedOnDaysOfWeek closedOnDaysOfWeek={place.closedOnDays}/>
								  </Stack>
						}
						{place.note && <Typography variant="body1" mt={1}>{place.note}</Typography>}
						<Stack direction="row" justifyContent="flex-end">
							  <IconButton disabled={inMemoryTrip.isCompleted || /*FALSY->*/ !!!place.anchoredOnDate || place.isFulfilled}
											  onClick={
													 () => swapOrderOfPlannedPlaces(uid, inMemoryTrip.itineraryKey, place.anchoredOnDate, true, place.placeKey, place)
											  }
							  >
									 <ArrowUpward/>
							  </IconButton>
							  <IconButton disabled={inMemoryTrip.isCompleted || /*FALSY->*/ !!!place.anchoredOnDate || place.isFulfilled}
											  onClick={
													 () => swapOrderOfPlannedPlaces(uid, inMemoryTrip.itineraryKey, place.anchoredOnDate, false, place.placeKey, place)
											  }
							  >
									 <ArrowDownward/>
							  </IconButton>
						</Stack>
				 </Card>
	  );
}